import { SelectItem } from '../../types/SelectItem'
import {
  FILTER_FIELDS,
  getFieldsForType,
  FilterParams,
} from './filterConfig'
import { FormCode } from '@kistill/shared/types'

export const mapSelectItemToIds = (selectItems?: SelectItem[] | null): string[] =>
  selectItems?.map((item) => String(item.id)) ?? []

export const mapSelectItemToIdsAsNumbers = (selectItems?: SelectItem[] | null): number[] =>
  selectItems?.map((item) => Number(item.id)).filter((id) => !isNaN(id)) ?? []

export const mapSelectItemToTitles = (selectItems?: SelectItem[] | null) =>
  selectItems?.map((item) => item.title) ?? []

export const handleString = (value?: string | null) => {
  return value ? value : ''
}

/**
 * Convert a radio group string value to a number array
 * Radio group returns empty string for "All" option, which should be undefined
 * Otherwise converts the string to a number and wraps it in an array
 */
export const radioGroupToNumberArray = (value?: string | null): number[] | undefined => {
  if (!value || value === '') return undefined
  const num = Number(value)
  if (isNaN(num)) return undefined
  return [num]
}

/**
 * Transform a field value based on its field definition
 */
const transformField = (
  fieldKey: string,
  value: unknown,
): string | string[] | number[] | undefined => {
  const fieldDef = FILTER_FIELDS[fieldKey]
  if (!fieldDef) return value as string | string[] | number[] | undefined

  switch (fieldDef.transform) {
    case 'selectItemToIds':
      return mapSelectItemToIds(value as SelectItem[] | null)
    case 'selectItemToIdsAsNumbers':
      return mapSelectItemToIdsAsNumbers(value as SelectItem[] | null)
    case 'selectItemToTitles':
      return mapSelectItemToTitles(value as SelectItem[] | null)
    case 'handleString':
      return handleString(value as string | null) || undefined
    case 'radioGroup':
      // Radio group returns empty string for "Öll" option, which should be undefined
      return (value as string) === '' ? undefined : (value as string | null) || undefined
    case 'radioGroupToNumberArray':
      return radioGroupToNumberArray(value as string | null)
    case 'string':
    default:
      return (value as string | null) || undefined
  }
}

/**
 * Build filter object from params using field definitions
 */
const buildFilterFromFields = (
  fields: readonly string[],
  params: FilterParams,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {}

  // Always include selectedGuids if present
  if (params['selectedGuids'] !== undefined) {
    result['selectedGuids'] = params['selectedGuids']
  }

  // Transform each field according to its definition
  for (const fieldKey of fields) {
    const fieldDef = FILTER_FIELDS[fieldKey]
    if (!fieldDef) continue

    const value = params[fieldKey as keyof FilterParams]
    if (value === undefined || value === null) continue

    const transformedValue = transformField(fieldKey, value)
    // Skip if transformed value is undefined, null, or empty string
    if (transformedValue === undefined || transformedValue === null) continue
    if (typeof transformedValue === 'string' && transformedValue.trim() === '') continue
    if (Array.isArray(transformedValue) && transformedValue.length === 0) continue

    const apiKey = fieldDef.apiKey || fieldKey

    // Special handling for items: titleSelectItem maps to "titles" (plural)
    if (fieldKey === 'titleSelectItem' && apiKey === 'title') {
      // Check if this is for items (which uses "titles" plural)
      // We'll handle this in the itemHandler
      result[apiKey] = transformedValue
    } else {
      result[apiKey] = transformedValue
    }
  }

  return result
}

/**
 * Generic handler for items and item subtypes
 * Special handling: titleSelectItem -> titles (plural) for items
 */
const itemHandler = (fields: readonly string[], params: FilterParams) => {
  const result = buildFilterFromFields(fields, params)

  // Special case: items use "titles" (plural) instead of "title"
  const titleSelectItem = params['titleSelectItem']
  if (titleSelectItem && Array.isArray(titleSelectItem)) {
    result['titles'] = mapSelectItemToIds(titleSelectItem)
    delete result['title']
  }

  return result
}

/**
 * Handle filter transformation based on type
 */
export const handleFilter = (type: string, params: FilterParams) => {
  if (!type) return {}

  // Get fields for this type
  const fields = getFieldsForType(type)
  if (fields.length === 0) return {}

  // Special handlers for items and item subtypes
  if (
    type === 'items' ||
    Object.values(FormCode).includes(type as FormCode)
  ) {
    return itemHandler(fields, params)
  }

  // Special case: contentAnalysis uses "titles" (plural) instead of "title"
  if (type === 'contentAnalysis') {
    const result = buildFilterFromFields(fields, params)
    const titleSelectItem = params['titleSelectItem']
    if (titleSelectItem && Array.isArray(titleSelectItem)) {
      result['titles'] = mapSelectItemToIds(titleSelectItem)
      delete result['title']
    }
    return result
  }

  // Generic handler for all other types
  return buildFilterFromFields(fields, params)
}
